import type { V10ProjectMode } from "../adapters/v10-phase-model.js";
import { V10_MODE_LABELS } from "../adapters/v10-phase-model.js";
import type { V10NewProjectFormState } from "../adapters/v10-new-project-form.js";
import { V10Btn } from "./V10Primitives.js";

export function V10TodayEntryButtons({
  onNewWebsite,
  onRelaunch,
  onExternalProof,
}: {
  readonly onNewWebsite: () => void;
  readonly onRelaunch: () => void;
  readonly onExternalProof: () => void;
}) {
  const entries: { readonly mode: V10ProjectMode; readonly onClick: () => void }[] = [
    { mode: "DEFAULT_GENERATION", onClick: onNewWebsite },
    { mode: "EXISTING_SITE_SCAN", onClick: onRelaunch },
    { mode: "EXTERNAL_PROOF_ONLY", onClick: onExternalProof },
  ];

  return (
    <div className="v10-today-entries" aria-label="Start new work">
      {entries.map(({ mode, onClick }) => (
        <button key={mode} type="button" className="v10-today-entry" onClick={onClick}>
          <strong>{V10_MODE_LABELS[mode]}</strong>
        </button>
      ))}
    </div>
  );
}

function updateForm(
  form: V10NewProjectFormState,
  patch: Partial<V10NewProjectFormState>,
): V10NewProjectFormState {
  return { ...form, ...patch };
}

export function V10NewWorkSheet({
  selectedMode,
  form,
  busy,
  validationIssues,
  onModeChange,
  onFormChange,
  onCreate,
  onCancel,
}: {
  readonly selectedMode: V10ProjectMode;
  readonly form: V10NewProjectFormState;
  readonly busy: boolean;
  readonly validationIssues: readonly string[];
  readonly onModeChange: (mode: V10ProjectMode) => void;
  readonly onFormChange: (form: V10NewProjectFormState) => void;
  readonly onCreate: () => void;
  readonly onCancel: () => void;
}) {
  const modes = ["DEFAULT_GENERATION", "EXISTING_SITE_SCAN", "EXTERNAL_PROOF_ONLY"] as const;

  return (
    <div className="v10-sheet-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="v10-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="v10-new-work-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="v10-new-work-title">New Work</h2>
        <p className="v10-muted">Choose how this project starts, then create the control room.</p>
        <div className="v10-sheet-modes" role="tablist" aria-label="Project mode">
          {modes.map((mode) => (
            <button
              key={mode}
              type="button"
              role="tab"
              aria-selected={selectedMode === mode}
              className={selectedMode === mode ? "active" : undefined}
              onClick={() => onModeChange(mode)}
            >
              {V10_MODE_LABELS[mode]}
            </button>
          ))}
        </div>
        {selectedMode === "DEFAULT_GENERATION" ? (
          <div className="v10-form-grid">
            <div>
              <label htmlFor="v10-sheet-package">Site package</label>
              <select
                id="v10-sheet-package"
                value={form.sitePackageId}
                disabled={busy}
                onChange={(e) => onFormChange(updateForm(form, { sitePackageId: e.target.value }))}
              >
                <option value="package-one-page">One page</option>
                <option value="package-basic-3">Basic 3 page</option>
                <option value="package-standard-5">Standard 5 page</option>
                <option value="package-growth-8">Growth 8 page</option>
                <option value="package-authority-10">Authority 10 page</option>
                <option value="package-authority-15">Authority 15 page</option>
              </select>
              <p className="v10-muted">
                Standard 5 needs richer client intake (multiple services, description, evidence). Thin profiles
                should start with Basic 3.
              </p>
            </div>
            <div>
              <label htmlFor="v10-sheet-lens">Visual lens</label>
              <select
                id="v10-sheet-lens"
                value={form.lensId}
                disabled={busy}
                onChange={(e) => onFormChange(updateForm(form, { lensId: e.target.value }))}
              >
                <option value="lens-flat-2">Flat 2 (default)</option>
                <option value="lens-glass">Glass (second lens)</option>
              </select>
            </div>
          </div>
        ) : null}
        {selectedMode === "DEFAULT_GENERATION" ? (
          <fieldset className="v10-form-fieldset">
            <legend>How to start</legend>
            <label>
              <input
                type="radio"
                name="v10-start-method"
                checked={form.startMethod === "intake"}
                onChange={() => onFormChange(updateForm(form, { startMethod: "intake" }))}
              />
              Send client intake link (real client path)
            </label>
            <label>
              <input
                type="radio"
                name="v10-start-method"
                checked={form.startMethod === "agency"}
                onChange={() => onFormChange(updateForm(form, { startMethod: "agency" }))}
              />
              Agency enters facts now (local proof — skip client intake)
            </label>
          </fieldset>
        ) : null}
        <div className="v10-form-grid">
          <div>
            <label htmlFor="v10-sheet-biz">Business name</label>
            <input
              id="v10-sheet-biz"
              value={form.businessName}
              disabled={busy}
              onChange={(e) => onFormChange(updateForm(form, { businessName: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="v10-sheet-client">Client entity</label>
            <input
              id="v10-sheet-client"
              value={form.clientEntityName}
              disabled={busy}
              onChange={(e) => onFormChange(updateForm(form, { clientEntityName: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="v10-sheet-scope">Scope summary</label>
            <input
              id="v10-sheet-scope"
              value={form.scopeSummary}
              disabled={busy}
              onChange={(e) => onFormChange(updateForm(form, { scopeSummary: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="v10-sheet-owner">Project owner</label>
            <input
              id="v10-sheet-owner"
              value={form.ownerLabel}
              disabled={busy}
              onChange={(e) => onFormChange(updateForm(form, { ownerLabel: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="v10-sheet-builder">Builder path</label>
            <select
              id="v10-sheet-builder"
              value={form.builderPath}
              disabled={busy}
              onChange={(e) =>
                onFormChange(
                  updateForm(form, {
                    builderPath: e.target.value as V10NewProjectFormState["builderPath"],
                  }),
                )
              }
            >
              <option value="ENREIN_GENERATE">Enrein Generate</option>
              <option value="EXTERNAL_SUPPORTED">Supported external builder</option>
            </select>
          </div>
          <div>
            <label htmlFor="v10-sheet-outcome">Target outcome</label>
            <input
              id="v10-sheet-outcome"
              value={form.targetOutcome}
              disabled={busy}
              onChange={(e) => onFormChange(updateForm(form, { targetOutcome: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="v10-sheet-service">Primary service</label>
            <input
              id="v10-sheet-service"
              value={form.primaryService}
              disabled={busy}
              onChange={(e) => onFormChange(updateForm(form, { primaryService: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="v10-sheet-area">Service area</label>
            <input
              id="v10-sheet-area"
              value={form.serviceArea}
              disabled={busy}
              onChange={(e) => onFormChange(updateForm(form, { serviceArea: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="v10-sheet-email">Contact email</label>
            <input
              id="v10-sheet-email"
              type="email"
              value={form.contactEmail}
              disabled={busy}
              onChange={(e) => onFormChange(updateForm(form, { contactEmail: e.target.value }))}
            />
          </div>
          {selectedMode === "EXISTING_SITE_SCAN" ? (
            <div className="v10-form-span">
              <label htmlFor="v10-sheet-scan-url">Website URL to scan</label>
              <input
                id="v10-sheet-scan-url"
                type="url"
                value={form.scanUrl}
                disabled={busy}
                onChange={(e) => onFormChange(updateForm(form, { scanUrl: e.target.value }))}
                placeholder="https://example.com"
              />
            </div>
          ) : null}
          {selectedMode === "EXTERNAL_PROOF_ONLY" ? (
            <div className="v10-form-span">
              <label htmlFor="v10-sheet-external-url">External site URL</label>
              <input
                id="v10-sheet-external-url"
                type="url"
                value={form.externalUrl}
                disabled={busy}
                onChange={(e) => onFormChange(updateForm(form, { externalUrl: e.target.value }))}
                placeholder="https://example.com"
              />
            </div>
          ) : null}
        </div>
        {validationIssues.length > 0 ? (
          <ul className="v10-validation-list">
            {validationIssues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        ) : null}
        <div className="v10-hero-actions">
          <V10Btn variant="primary" disabled={busy} onClick={onCreate}>
            {busy ? "Creating…" : "Create project"}
          </V10Btn>
          <V10Btn variant="line" onClick={onCancel} disabled={busy}>
            Cancel
          </V10Btn>
        </div>
      </div>
    </div>
  );
}
